<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class="text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Annual Fundraising Goals (3 Years)</h3>
        </div>
        <div v-if="isLoading" class="text-gray-600 font-sans py-4">
            <p>Loading data...</p>
        </div>
        <div v-else-if="errorMessage" class="p-4 bg-red-50 font-sans border border-red-200 rounded-md mb-4">
            <p class="text-red-700">{{ errorMessage }}</p>
        </div>
        <div v-else>
            <AnnualFundraisingSummary class="mt-6" :yearsData="yearsData" />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useStepValidation } from '@/composables/useStepValidation';
import AnnualFundraisingSummary from '@/components/AnnualFundraisingSummary.vue';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

const isLoading = ref(true);
const errorMessage = ref("");
const yearsData = ref([
  { expenses: 0, earnedRevenue: 0, investmentIncome: 0, govAppropriations: 0, inKindGoods: 0 },
  { expenses: 0, earnedRevenue: 0, investmentIncome: 0, govAppropriations: 0, inKindGoods: 0 },
  { expenses: 0, earnedRevenue: 0, investmentIncome: 0, govAppropriations: 0, inKindGoods: 0 },
]);

const authStore = useAuthStore();

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";
    await authStore.fetchUserData();
    if (!authStore.user?.accessToken) {
      throw new Error('Unable to load user authentication. Please try refreshing the page.');
    }
    const res = await fetch(`${global.API_URL}/total_fundraising_goal/annual_fundraising_goals_3years`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }
    const data = await res.json();
    // Prepare yearsData array for AnnualFundraisingSummary
    const revenue = data.revenue_sums_by_type_and_year || {};
    yearsData.value = [1, 2, 3].map((yearIdx) => {
      const yearKey = `year_${yearIdx}`;
      return {
        expenses: data[`total_annual_expenses_year_${yearIdx}`] || 0,
        earnedRevenue: revenue['Earned Revenue'] ? revenue['Earned Revenue'][yearKey] || 0 : 0,
        investmentIncome: revenue['Investment Income'] ? revenue['Investment Income'][yearKey] || 0 : 0,
        govAppropriations: revenue['Government Appropriations'] ? revenue['Government Appropriations'][yearKey] || 0 : 0,
        inKindGoods: revenue['In-Kind Goods/Services'] ? revenue['In-Kind Goods/Services'][yearKey] || 0 : 0,
      };
    });
  } catch (error) {
    errorMessage.value = error.message || 'Failed to load fundraising goals data.';
    console.error(error);
  } finally {
    isLoading.value = false;
  }
});

// Use the validation composable with default settings
useStepValidation();
</script>

<style scoped>
/* Inherit scrollbar styles from StepsNav component */
.right-content-grid-vs::-webkit-scrollbar {
    width: 8px;
}

.right-content-grid-vs::-webkit-scrollbar-track {
    background: #EBE8E1;
}

.right-content-grid-vs::-webkit-scrollbar-thumb {
    background: #00423D;
    border-radius: 6px;
}
</style> 